/*
 * Field: label, hint, control, error, and the ids that tie them together.
 *
 * On screen these four things are associated by proximity. Nothing associates
 * them for anybody else unless the ids are wired, and the failure is specific:
 * a validation message rendered two lines under an input is, to a screen
 * reader, an unrelated paragraph somewhere else on the page. The person is told
 * their entry was rejected and never told why. So this component owns the
 * wiring rather than leaving it to each call site to remember.
 *
 * The wiring travels by context rather than by `cloneElement`. cloneElement
 * injects props into whatever element it is handed and does nothing at all when
 * that is a fragment, or a component that does not forward unknown props down
 * onto its input. The error renders, the form looks correct, and the
 * description is silently absent. Context is read deliberately by each control,
 * so a control that has not opted in is visibly unwired when you read it.
 *
 * The error paragraph is deliberately not `role="alert"`. A form with four
 * invalid fields would fire four interruptions at once, in an order nobody
 * chose, and because the same text is also in `aria-describedby` each field
 * would then announce its error a second time when focus reached it. The
 * submission handler owns the single announcement of "four problems"; this
 * component owns the association.
 */

import { useId } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import { joinIds } from '../aria'
import { FieldContext } from './fieldContext'
import styles from './Field.module.css'

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: string
  hint?: ReactNode
  /** Present only when the value is wrong. Presence is what sets aria-invalid. */
  error?: ReactNode
  required?: boolean
  /**
   * Hides the label visually and leaves it in the accessibility tree. For a
   * filter bar where the column heading above already says "Status" and a
   * second visible label would be noise.
   */
  labelHidden?: boolean
  /**
   * Set when the Field wraps several controls rather than one. The label then
   * names a group instead of pointing at a single input, because `<label for>`
   * can only ever reach one element and pointing it at the first radio in a set
   * says the group's name belongs to that one option.
   */
  group?: boolean
  children: ReactNode
}

export function Field({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  group = false,
  children,
  className,
  ...rest
}: FieldProps) {
  const base = useId()
  const controlId = `${base}-control`
  const labelId = `${base}-label`
  const hintId = `${base}-hint`
  const errorId = `${base}-error`

  const invalid = error !== undefined && error !== null && error !== false
  /*
   * Error first. `aria-describedby` is read in the order given, not in DOM
   * order, and somebody who has just been told their entry is invalid wants the
   * reason before they are read the general guidance again.
   */
  const describedBy = joinIds(invalid && errorId, hint ? hintId : undefined)

  const rootClassName = [styles.field, className].filter(Boolean).join(' ')
  const labelClassName = [styles.label, labelHidden ? 'visually-hidden' : ''].filter(Boolean).join(' ')

  /*
   * The asterisk is decoration and is hidden. Required is announced from
   * `aria-required` on the control instead, which every screen reader already
   * says in its own words and in its own language. Appending "(required)" to
   * the label as well, which is the usual reflex, produces "Locale required
   * required" on any reader that also reports the ARIA state, and the second
   * one sounds like a stutter in the page rather than emphasis.
   */
  const labelContent = (
    <>
      {label}
      {required ? (
        <span className={styles.requiredMark} aria-hidden="true">
          *
        </span>
      ) : null}
    </>
  )

  const body = (
    <>
      {group ? (
        <span className={labelClassName} id={labelId}>
          {labelContent}
        </span>
      ) : (
        <label className={labelClassName} htmlFor={controlId}>
          {labelContent}
        </label>
      )}

      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}

      <div className={styles.control}>
        <FieldContext.Provider value={{ id: controlId, describedBy, invalid, required }}>
          {children}
        </FieldContext.Provider>
      </div>

      {invalid ? (
        <p className={styles.error} id={errorId}>
          {error}
        </p>
      ) : null}
    </>
  )

  if (group) {
    return (
      <div
        className={rootClassName}
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        {...rest}
      >
        {body}
      </div>
    )
  }

  return (
    <div className={rootClassName} {...rest}>
      {body}
    </div>
  )
}
